import React, { useState, useEffect } from 'react';
import { graphql } from 'gatsby';
//@ts-ignore
import * as styles from './imageWithCopySlider.module.css';
//@ts-ignore
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import {
    KontentStringValue,
    KontentMedia,
    KontentRichText,
    CTAConfiguration,
    BrandColor,
} from '../../../types/KontentProps';
import { RichTextEl } from '../Global/RichText';
import {
    getMediaData,
    getMultiChoice,
    getColor,
    optimizeKontentImageUrl,
    buildKontentSrcSet,
    imagePreset,
} from '../Global/DataUtils';
import CtaLink from '../Global/CtaLink';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import ArrowLgButton from '../Global/ArrowLgButton';
import { KontentRadioMultiChoice } from '../../../types/KontentProps';

type ImageCopySlideProps = {
    id: string;
    elements: CTAConfiguration & {
        headline: KontentStringValue;
        horizontal_image_position: KontentRadioMultiChoice<'left' | 'right'>;
        primary_image: KontentMedia;
        secondary_image: KontentMedia;
        headline_line_break: KontentRadioMultiChoice<'true' | 'false'>;
        sub_title: KontentStringValue;
        image: KontentMedia;
        description: KontentRichText;
        content_alignment: KontentRadioMultiChoice<'left' | 'center' | 'right'>;
    };
};

interface Props {
    kontentItemId?: string;
    image_copy_slide: {
        value: ImageCopySlideProps[];
    };
    anchor_name: KontentStringValue;
    content_panel__content_panel_color?: BrandColor;
    content_panel__content_panel_font_color?: BrandColor;
    copy_alignment?: KontentRadioMultiChoice<'left' | 'center'>;
}

const ImageWithCopySlider: React.FC<Props> = ({
    image_copy_slide,
    anchor_name,
    content_panel__content_panel_color,
    content_panel__content_panel_font_color,
    copy_alignment,
    kontentItemId,
}) => {
    const breakpoints = useBreakpoint();
    const contentLeftAlign = getMultiChoice(copy_alignment) === 'left';
    const mobileImgdiv = breakpoints.sm
        ? {}
        : { width: '100%', height: '100%' };
    const noOfPanels = image_copy_slide.value.length;
    const [currentPanel, setCurrentPanel] = useState(0);
    const strokeColor = '#505251';
    const contentPanelColor = getColor(content_panel__content_panel_color);
    const contentPanelFontColor = getColor(
        content_panel__content_panel_font_color
    );
    const textColor = contentPanelFontColor ? contentPanelFontColor : undefined;
    const backgroundColor = contentPanelColor ? contentPanelColor : undefined;

    const getPanelData = (panel: ImageCopySlideProps) => {
        let WindowActionStructure =
            panel.elements?.cta_configuration__cta_window_action;
        let ctaWindowAction = getMultiChoice(WindowActionStructure);

        if (ctaWindowAction == null && WindowActionStructure?.value[0]?.name) {
            ctaWindowAction = WindowActionStructure?.value[0]?.name
                ?.toLowerCase()
                ?.split(' ')
                ?.join('_');
        }
        const ctaTarget = ctaWindowAction === 'new_window' ? '_blank' : '_self';
        const slideMetaData = {
            headline: panel.elements?.headline?.value,
            image: getMediaData(panel.elements?.primary_image),
            secondaryImage: getMediaData(panel.elements?.secondary_image),
            description: panel.elements?.description,
            horizontalImagePosition:
                panel.elements?.horizontal_image_position?.value[0]?.name,
            headlineLineBreak:
                panel?.elements?.headline_line_break?.value[0]?.name,
            cta_label: panel.elements?.cta_configuration__cta_label?.value,
            cta_location: BuildLinkHref(
                panel.elements?.cta_configuration__cta_location
            ),
            cta_window_action: ctaWindowAction,
            cta_anchor_point:
                panel?.elements?.cta_configuration__cta_location_anchor_point
                    ?.value || undefined,
            cta_target: ctaTarget,
        };
        return slideMetaData;
    };

    const [currentPanelData, setCurrentPanelData] = useState(
        getPanelData(image_copy_slide.value[currentPanel])
    );

    const handleClickPrev = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        const newPanel = currentPanel - 1;
        setCurrentPanel(newPanel);
        setCurrentPanelData(getPanelData(image_copy_slide.value[newPanel]));
    };

    const handleClickNext = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        const newPanel = currentPanel + 1;
        setCurrentPanel(newPanel);
        setCurrentPanelData(getPanelData(image_copy_slide.value[newPanel]));
    };

    const renderArrowPrev = () => {
        if (currentPanel > 0) {
            return (
                <ArrowLgButton
                    direction="left"
                    strokeColor={
                        contentPanelFontColor
                            ? contentPanelFontColor
                            : strokeColor
                    }
                    className={styles.prev}
                    handleClick={handleClickPrev}
                />
            );
        }
    };

    const renderArrowNext = () => {
        if (currentPanel < noOfPanels - 1) {
            return (
                <ArrowLgButton
                    direction="right"
                    strokeColor={
                        contentPanelFontColor
                            ? contentPanelFontColor
                            : strokeColor
                    }
                    className={styles.next}
                    handleClick={handleClickNext}
                />
            );
        }
    };

    function renderImageContainer() {
        return (
            <div className={styles.imagePanel}>
                <div className={styles.primaryImage}>
                    <div
                        style={{
                            position: breakpoints.sm ? 'absolute' : 'relative',
                            top: 0,
                            ...mobileImgdiv,
                        }}
                    >
                        {currentPanelData.image?.url ? (
                            <div data-kontent-element-codename="primary_image">
                                <img
                                    src={optimizeKontentImageUrl(
                                        currentPanelData.image.url,
                                        {
                                            ...imagePreset.halfWidth.mobile,
                                            q: 95,
                                        }
                                    )}
                                    srcSet={buildKontentSrcSet(
                                        currentPanelData.image.url,
                                        imagePreset.halfWidth.mobile.w,
                                        {
                                            fm: 'webp' as const,
                                            q: 95,
                                        }
                                    )}
                                    sizes="100vw"
                                    alt={
                                        currentPanelData.image?.description ||
                                        currentPanelData.image?.name
                                    }
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                        ) : currentPanelData.secondaryImage?.url ? (
                            <div data-kontent-element-codename="secondary_image">
                                <img
                                    src={optimizeKontentImageUrl(
                                        currentPanelData.secondaryImage.url,
                                        {
                                            ...imagePreset.halfWidth.mobile,
                                            q: 95,
                                        }
                                    )}
                                    srcSet={buildKontentSrcSet(
                                        currentPanelData.secondaryImage.url,
                                        imagePreset.halfWidth.mobile.w,
                                        {
                                            fm: 'webp' as const,
                                            q: 95,
                                        }
                                    )}
                                    sizes="100vw"
                                    alt={
                                        currentPanelData.secondaryImage
                                            ?.description ||
                                        currentPanelData.secondaryImage?.name
                                    }
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                        ) : (
                            <></>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    function renderMobileComponent() {
        return (
            <div className={styles.panelContainer}>
                <div
                    // style={currentPanelData.horizontalImagePosition != "Float Top" ? {flexDirection:'column-reverse' } : null}
                    className={styles.panel}
                    key={currentPanel}
                    id={currentPanelData.cta_anchor_point}
                >
                    {renderArrowPrev()}
                    {renderArrowNext()}

                    {currentPanelData.headline && (
                        <div
                            style={
                                contentPanelFontColor
                                    ? { color: contentPanelFontColor }
                                    : {}
                            }
                            className={`${styles.panelTitle} ${
                                contentLeftAlign ? styles.LeftAlign : ''
                            }`}
                        >
                            {currentPanelData.headline}
                        </div>
                    )}

                    {renderImageContainer()}
                    {currentPanelData.description && (
                        <div
                            className={`${styles.description} ${
                                contentLeftAlign ? styles.LeftAlign : ''
                            }`}
                        >
                            <RichTextEl
                                richText={currentPanelData.description}
                                elementCodename="description"
                            />
                        </div>
                    )}
                </div>
                {currentPanelData.cta_location && (
                    <div className={styles.ctaContainer}>
                        <CtaLink
                            href={currentPanelData.cta_location}
                            target={currentPanel.cta_target}
                            color={textColor ? textColor : undefined}
                        >
                            {currentPanelData.cta_label}
                        </CtaLink>
                    </div>
                )}
            </div>
        );
    }

    function renderDesktopComponent() {
        return (
            <div className={styles.panelContainer}>
                <div
                    className={styles.panel}
                    style={
                        currentPanelData.horizontalImagePosition == 'Right'
                            ? { flexDirection: 'row' }
                            : { flexDirection: 'row-reverse' }
                    }
                >
                    {renderArrowPrev()}
                    {renderArrowNext()}

                    <div className={styles.imageWrapper}>
                        <div className={styles.primaryImage}>
                            {currentPanelData.image?.url ? (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                    }}
                                    data-kontent-element-codename="primary_image"
                                >
                                    <img
                                        src={optimizeKontentImageUrl(
                                            currentPanelData.image.url,
                                            {
                                                ...imagePreset.halfWidth
                                                    .desktop,
                                                q: 95,
                                            }
                                        )}
                                        srcSet={buildKontentSrcSet(
                                            currentPanelData.image.url,
                                            imagePreset.halfWidth.desktop.w,
                                            {
                                                fm: 'webp' as const,
                                                q: 95,
                                            }
                                        )}
                                        sizes="(max-width: 991px) 100vw, 50vw"
                                        alt={
                                            currentPanelData.image
                                                ?.description ||
                                            currentPanelData.image?.name
                                        }
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </div>
                            ) : currentPanelData.secondaryImage?.url ? (
                                <div data-kontent-element-codename="secondary_image">
                                    <img
                                        src={optimizeKontentImageUrl(
                                            currentPanelData.secondaryImage.url,
                                            {
                                                ...imagePreset.halfWidth
                                                    .desktop,
                                                q: 95,
                                            }
                                        )}
                                        srcSet={buildKontentSrcSet(
                                            currentPanelData.secondaryImage.url,
                                            imagePreset.halfWidth.desktop.w,
                                            {
                                                fm: 'webp' as const,
                                                q: 95,
                                            }
                                        )}
                                        sizes="(max-width: 991px) 100vw, 50vw"
                                        alt={
                                            currentPanelData.secondaryImage
                                                ?.description ||
                                            currentPanelData.secondaryImage
                                                ?.name
                                        }
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </div>
                            ) : (
                                <></>
                            )}
                        </div>
                    </div>
                    <div className={styles.panelData}>
                        {currentPanelData.image?.url &&
                            currentPanelData.secondaryImage?.url && (
                                <div data-kontent-element-codename="secondary_image">
                                    <img
                                        src={optimizeKontentImageUrl(
                                            currentPanelData.secondaryImage.url,
                                            {
                                                ...imagePreset.halfWidth
                                                    .desktop,
                                                q: 95,
                                            }
                                        )}
                                        srcSet={buildKontentSrcSet(
                                            currentPanelData.secondaryImage.url,
                                            imagePreset.halfWidth.desktop.w,
                                            {
                                                fm: 'webp' as const,
                                                q: 95,
                                            }
                                        )}
                                        sizes="(max-width: 991px) 100vw, 50vw"
                                        alt={
                                            currentPanelData.secondaryImage
                                                ?.description ||
                                            currentPanelData.secondaryImage
                                                ?.name
                                        }
                                        className={styles.imagesecond}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </div>
                            )}
                        <p></p>
                        <div className={styles.panelContent}>
                            {currentPanelData.headline && (
                                <div
                                    style={
                                        contentPanelFontColor
                                            ? { color: contentPanelFontColor }
                                            : {}
                                    }
                                    className={`${styles.panelTitle} ${
                                        contentLeftAlign ? styles.LeftAlign : ''
                                    }`}
                                >
                                    {currentPanelData.headline}
                                </div>
                            )}

                            {currentPanelData.headlineLineBreak == 'Yes' && (
                                <hr />
                            )}
                            {currentPanelData.description && (
                                <div
                                    className={`${styles.description} ${
                                        contentLeftAlign ? styles.LeftAlign : ''
                                    }`}
                                >
                                    <RichTextEl
                                        richText={currentPanelData.description}
                                        elementCodename="description"
                                    />
                                </div>
                            )}
                            {currentPanelData.cta_location && (
                                <div className={styles.ctaContainer}>
                                    <CtaLink
                                        href={currentPanelData.cta_location}
                                        target={currentPanel.cta_target}
                                        color={
                                            textColor ? textColor : undefined
                                        }
                                    >
                                        {currentPanelData.cta_label}
                                    </CtaLink>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <section
            className={styles.wrapper}
            style={{
                color: textColor,
                borderColor: textColor,
                background: backgroundColor,
            }}
            data-kontent-item-id={kontentItemId}
        >
            {breakpoints.l ? renderDesktopComponent() : renderMobileComponent()}
        </section>
    );
};

export default ImageWithCopySlider;

export const fragmentImageWithCopySlider = graphql`
    fragment ImageWithCopySlider on kontent_item_component___image_with_copy_slider {
        elements {
            anchor_name {
                value
            }
            copy_alignment {
                value {
                    codename
                }
            }
            content_panel__content_panel_font_color {
                value {
                    ... on kontent_item_list___brand_colors {
                        id
                        elements {
                            color_hex_value {
                                value
                            }
                        }
                    }
                }
            }
            content_panel__content_panel_color {
                value {
                    ... on kontent_item_list___brand_colors {
                        id
                        elements {
                            color_hex_value {
                                value
                            }
                        }
                    }
                }
            }
            image_copy_slide {
                value {
                    ... on kontent_item_content_item___image_copy_slide {
                        id
                        elements {
                            headline {
                                value
                            }
                            description {
                                ...kontentRichText
                            }
                            horizontal_image_position {
                                value {
                                    name
                                }
                            }
                            primary_image {
                                ...kontentImage
                            }
                            secondary_image {
                                ...kontentImage
                            }
                            headline_line_break {
                                value {
                                    name
                                }
                            }
                            cta_configuration__cta_window_action {
                                value {
                                    name
                                }
                            }
                            cta_configuration__cta_location_anchor_point {
                                value
                            }
                            cta_configuration__cta_location {
                                ...kontentCtaLocation
                            }
                            cta_configuration__cta_label {
                                value
                            }
                        }
                    }
                }
            }
        }
    }
`;
